import { useContext } from 'react';
import { useCreateMachine } from 'fsm';
import { useAlerts } from 'breeze-intelligence-kit';
import { useCurrentUser } from 'policy-lib';
import { machine } from './machine';
import { EnrichmentSettingsContext } from './context';
import { ENRICHMENT_SETTINGS_KEYS } from './enrichmentSettings';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Base settings object with all enrichment settings initialized to `false` to
 * initialize the machine.
 *
 * @returns {SettingsData} An object where each key corresponds to an enrichment setting
 * and its value is set to `false`.
 *
 * @example
 * const baseSettings = {
 *   companyEnrichmentAuto: false,
 *   contactEnrichmentManual: false
 * };
 */
const baseSettings = ENRICHMENT_SETTINGS_KEYS.reduce((acc, key) => {
  acc[key] = false;
  return acc;
}, {});
export const EnrichmentSettingsProvider = ({
  children
}) => {
  const currentUser = useCurrentUser();
  const {
    addAlertDanger,
    addAlertSuccess
  } = useAlerts();
  const enrichmentSettingsMachine = useCreateMachine(machine, {
    initialContext: {
      addAlertDanger,
      addAlertSuccess,
      isLoaded: false,
      settings: Object.assign({}, baseSettings)
    },
    isReady: currentUser.isLoaded
  });
  return /*#__PURE__*/_jsx(EnrichmentSettingsContext.Provider, {
    value: enrichmentSettingsMachine,
    children: children
  });
};
export function useEnrichmentSettings() {
  const settingsMachine = useContext(EnrichmentSettingsContext);
  if (!settingsMachine) {
    throw new Error('useEnrichmentSettings must be used within a EnrichmentSettingsProvider');
  }
  const {
    state,
    send
  } = settingsMachine;
  const isLoading = state.hasTag('loading');
  const isLoaded = !isLoading;
  const onToggleSetting = key => {
    send({
      type: 'TOGGLE_SETTING',
      data: {
        key
      }
    });
  };
  return {
    isLoaded,
    isLoading,
    onToggleSetting,
    settings: state.context.settings,
    featureStates: state.context.featureStates
  };
}